import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { JustifiedAction } from '../actions/JustifiedAction';
import { formatMoney } from '../cashbacks/formatMoney';
import {
  FRAUD_STATUS_COLOR,
  FRAUD_STATUS_LABEL,
  formatRisk,
} from './fraudState';
import type { FraudRow } from './fraudQueueModel';
import { suspensionEndAfter } from '../suspensions/suspensionsModel';
import {
  REASON_CODE_OPTIONS,
  SUSPENSION_PERIOD_OPTIONS,
} from '../suspensions/suspensionState';

/**
 * One flagged user in the fraud queue: identity, the risk score and fraud status as
 * DISPLAYED FACTS, the evidence panel (the stress markers that genuinely exist), and
 * the guarded recorded-decision actions.
 *
 * Every action routes through the performStatusTransition chokepoint (via
 * JustifiedAction, which captures the reason), so the WHY lands in the audit trail
 * and no action mutates value (a ban is a status, there is no balance field to
 * write). The action set is gated by REVERSIBILITY (D-fraud-queue): a fraud reviewer
 * can suspend, escalate, ban and reinstate a suspension, but reversing a ban
 * (users:unban) is super_admin-only.
 */

const StressList = ({
  title,
  empty,
  children,
  testId,
}: {
  title: string;
  empty: boolean;
  children: React.ReactNode;
  testId: string;
}) => (
  <Box sx={{ mb: 1 }} data-testid={testId}>
    <Typography variant="caption" color="text.secondary">
      {title}
    </Typography>
    {empty ? (
      <Typography variant="body2" color="text.secondary">
        None
      </Typography>
    ) : (
      <Stack spacing={0.25}>{children}</Stack>
    )}
  </Box>
);

export const FraudUserCard = ({ row, now }: { row: FraudRow; now: number }) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();
  const ctx = { role, accessibleRegions };
  const can = (capability: Parameters<typeof hasAccess>[1]['capability']) =>
    hasAccess(ctx, { capability });

  const prev = {
    id: row.id,
    fraudStatus: row.fraudStatus,
    suspensionEndsAt: row.suspensionEndsAt,
    fraudReason: row.fraudReason,
    suspensionReasonCode: row.suspensionReasonCode,
    appealStatus: row.appealStatus,
  };

  // Lifting an account to active clears every live hold field (D-suspension-flow):
  // an active user carries no reason code, no period and no free-text reason, and a
  // pending appeal is resolved. The audit trail keeps the full history.
  const liftToActive = {
    fraudStatus: 'active',
    suspensionEndsAt: null,
    fraudReason: null,
    suspensionReasonCode: null,
    appealStatus:
      row.appealStatus === 'pending' ? 'resolved' : row.appealStatus,
  };

  return (
    <Card
      variant="outlined"
      data-testid={`fraud-user-${row.id}`}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
        >
          <Box sx={{ minWidth: 200 }}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {row.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.email} | {row.market.toUpperCase()}
            </Typography>
          </Box>
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={`Risk ${formatRisk(row.riskScore)}`}
            data-testid={`fraud-risk-${row.id}`}
          />
          <Chip
            size="small"
            variant="outlined"
            color={FRAUD_STATUS_COLOR[row.fraudStatus]}
            label={FRAUD_STATUS_LABEL[row.fraudStatus]}
          />
          {row.escalated && (
            <Chip
              size="small"
              color="warning"
              variant="filled"
              label="Escalated"
            />
          )}
          {row.fraudStatus === 'suspended' && row.suspensionEndsAt && (
            <Typography variant="caption" color="text.secondary">
              until {row.suspensionEndsAt.slice(0, 10)}
            </Typography>
          )}
        </Stack>

        {row.fraudReason && (
          <Typography
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ mt: 1 }}
          >
            Reason: {row.fraudReason}
          </Typography>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Evidence: the stress markers that genuinely exist, as FACTS not scores. */}
        <Box data-testid={`fraud-evidence-${row.id}`}>
          <StressList
            title="High-value orders"
            empty={row.evidence.highValueTxns.length === 0}
            testId={`fraud-evidence-txns-${row.id}`}
          >
            {row.evidence.highValueTxns.map((t) => (
              <Typography key={t.id} variant="body2">
                {formatMoney(t.grossAmount, t.currency)} | {t.status} |{' '}
                {t.occurredAt.slice(0, 10)}
              </Typography>
            ))}
          </StressList>
          <StressList
            title="SLA-breached payouts"
            empty={row.evidence.breachedPayouts.length === 0}
            testId={`fraud-evidence-payouts-${row.id}`}
          >
            {row.evidence.breachedPayouts.map((c) => (
              <Typography key={c.id} variant="body2">
                {formatMoney(c.amount, c.currency)} | {c.status} | breached{' '}
                {c.slaBreachedAt.slice(0, 10)}
              </Typography>
            ))}
          </StressList>
          <StressList
            title="Manually attributed orders"
            empty={row.evidence.orphanAttributions.length === 0}
            testId={`fraud-evidence-orphans-${row.id}`}
          >
            {row.evidence.orphanAttributions.map((t) => (
              <Typography key={t.id} variant="body2">
                {formatMoney(t.grossAmount, t.currency)} | {t.attributionStatus}
              </Typography>
            ))}
          </StressList>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Recorded-decision actions, gated by reversibility. */}
        <Stack direction="row" spacing={2} useFlexGap flexWrap="wrap">
          {row.fraudStatus === 'active' && (
            <>
              {can('users:suspend') && (
                <JustifiedAction
                  resource="users"
                  capability="users:suspend"
                  id={row.id}
                  label="Suspend"
                  color="warning"
                  dialogTitle="Suspend this account"
                  dialogIntro="A temporary hold. Pick a period and a reason code, and record why."
                  reasonCodes={REASON_CODE_OPTIONS}
                  periods={SUSPENSION_PERIOD_OPTIONS}
                  buildData={(reason, _member, code, days) => ({
                    fraudStatus: 'suspended',
                    suspensionEndsAt: suspensionEndAfter(now, days ?? 30),
                    suspensionReasonCode: code,
                    fraudReason: reason,
                  })}
                  previousData={prev}
                  successMessage="Account suspended"
                />
              )}
              {can('users:ban') && (
                <JustifiedAction
                  resource="users"
                  capability="users:ban"
                  id={row.id}
                  label="Ban"
                  color="error"
                  dialogTitle="Ban this account"
                  dialogIntro="A terminal block. Pick a reason code. Only a super_admin can reverse it. Record why."
                  reasonCodes={REASON_CODE_OPTIONS}
                  buildData={(reason, _member, code) => ({
                    fraudStatus: 'banned',
                    suspensionEndsAt: null,
                    suspensionReasonCode: code,
                    fraudReason: reason,
                  })}
                  previousData={prev}
                  successMessage="Account banned"
                />
              )}
              {can('users:escalate') && (
                <JustifiedAction
                  resource="users"
                  capability="users:escalate"
                  id={row.id}
                  label="Escalate"
                  color="primary"
                  dialogTitle="Escalate for higher review"
                  dialogIntro="Flag for senior review without changing the status. Record why."
                  buildData={(reason) => ({
                    escalated: true,
                    fraudReason: reason,
                  })}
                  previousData={{
                    id: row.id,
                    escalated: row.escalated,
                    fraudReason: row.fraudReason,
                  }}
                  successMessage="Escalated for review"
                />
              )}
            </>
          )}

          {row.fraudStatus === 'suspended' && (
            <>
              {can('users:reinstate') && (
                <JustifiedAction
                  resource="users"
                  capability="users:reinstate"
                  id={row.id}
                  label="Reinstate"
                  color="primary"
                  dialogTitle="Lift the suspension"
                  dialogIntro="Return the account to active. This clears the live hold and resolves any appeal. Record why."
                  buildData={() => liftToActive}
                  previousData={prev}
                  successMessage="Suspension lifted"
                />
              )}
              {can('users:ban') && (
                <JustifiedAction
                  resource="users"
                  capability="users:ban"
                  id={row.id}
                  label="Ban"
                  color="error"
                  dialogTitle="Ban this account"
                  dialogIntro="A terminal block. Pick a reason code. Only a super_admin can reverse it. Record why."
                  reasonCodes={REASON_CODE_OPTIONS}
                  buildData={(reason, _member, code) => ({
                    fraudStatus: 'banned',
                    suspensionEndsAt: null,
                    suspensionReasonCode: code,
                    fraudReason: reason,
                  })}
                  previousData={prev}
                  successMessage="Account banned"
                />
              )}
            </>
          )}

          {row.fraudStatus === 'banned' &&
            (can('users:unban') ? (
              <JustifiedAction
                resource="users"
                capability="users:unban"
                id={row.id}
                label="Reinstate"
                color="primary"
                dialogTitle="Reverse this ban"
                dialogIntro="Return a banned account to active. This clears the live hold and resolves any appeal. A super_admin only reversal. Record why."
                buildData={() => liftToActive}
                previousData={prev}
                successMessage="Ban reversed"
              />
            ) : (
              <Typography variant="caption" color="text.secondary">
                A ban can be reversed by a super_admin only.
              </Typography>
            ))}
        </Stack>
      </CardContent>
    </Card>
  );
};
